import { Link } from 'react-router-dom';
import { LegalPageLayout } from './LegalPageLayout';

const EFFECTIVE_DATE = 'March 1, 2026';
const VERSION = '1.0';

export default function AiDisclosure() {
  return (
    <LegalPageLayout>
      <h1>AI Disclosure Addendum</h1>
      <p><strong>No Doubles Golf Co.</strong></p>
      <p><strong>Version:</strong> {VERSION}</p>
      <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p>
        This AI Disclosure Addendum supplements and is incorporated into the No Doubles Golf Co.{' '}
        <Link to="/terms">Terms of Service</Link> ("Terms"). Capitalized terms not defined here
        have the meanings assigned in the Terms.
      </p>

      <hr />

      <h2>1. Purpose of This Disclosure</h2>
      <p>
        No Doubles uses artificial intelligence and machine learning technology as part of the
        Service. This Addendum explains how AI is used, what its limitations are, and what you
        should understand before relying on any AI-generated output.
      </p>

      <hr />

      <h2>2. How AI Is Used</h2>
      <p>The Service uses AI to:</p>
      <ul>
        <li>Analyze video recordings of your golf swing by detecting body position, joint angles, and movement sequences</li>
        <li>Identify potential swing characteristics and movement patterns</li>
        <li>Generate drill recommendations and structured practice plans</li>
        <li>Provide mobility screening observations based on movement assessments</li>
        <li>Track progress by comparing video analyses over time</li>
      </ul>
      <p>
        AI processing occurs on the video and data you submit. Results are generated automatically
        without human review.
      </p>

      <hr />

      <h2>3. What AI Is Not</h2>
      <h3>3.1 Not Professional Instruction</h3>
      <p>
        AI Outputs do not constitute professional golf instruction, coaching, or training. The
        Service does not replicate the judgment, observation, or personalized assessment of a
        certified golf instructor, PGA professional, or sports coach.
      </p>
      <h3>3.2 Not Medical or Therapeutic Advice</h3>
      <p>
        AI Outputs — including mobility screening results, movement assessments, and exercise
        recommendations — are not medical advice, physical therapy, biomechanical diagnosis, or
        therapeutic treatment. The Service does not diagnose medical conditions, prescribe
        treatment, or evaluate your fitness for physical activity.
      </p>
      <h3>3.3 Not a Substitute for Human Judgment</h3>
      <p>
        AI Outputs are automated observations generated by a machine learning system. They reflect
        patterns detected in your submitted data. They do not reflect human expertise, clinical
        judgment, or personalized professional evaluation. You are responsible for applying your
        own judgment before acting on any AI Output.
      </p>

      <hr />

      <h2>4. Limitations and Accuracy</h2>
      <h3>4.1 Inherent Limitations</h3>
      <p>You acknowledge that:</p>
      <ul>
        <li>AI may produce outputs that are inaccurate, incomplete, misleading, or inappropriate for your circumstances</li>
        <li>AI analyzes limited data and does not have access to your full physical history or medical conditions</li>
        <li>AI outputs may vary based on video quality, camera angle, lighting, and clothing</li>
        <li>AI may not detect conditions or risks that a human instructor or medical professional would identify</li>
        <li>AI models are continuously evolving, and outputs may change over time</li>
      </ul>
      <h3>4.2 No Guaranteed Accuracy</h3>
      <p>
        No Doubles does not warrant that AI Outputs are accurate, correct, complete, or suitable
        for any particular purpose.
      </p>
      <h3>4.3 AI Hallucination Risk</h3>
      <p>
        AI systems may generate outputs that appear confident but are factually incorrect or
        biomechanically unsound. You should not assume that an AI Output is correct solely because
        it is presented with specificity or confidence.
      </p>

      <hr />

      <h2>5. Your Responsibilities</h2>
      <ul>
        <li>Evaluate AI Outputs critically and use your own judgment</li>
        <li>Do not perform any exercise that causes pain or feels unsafe, regardless of whether the Service recommended it</li>
        <li>Consult a qualified professional if you have questions about any recommendation</li>
        <li>Do not rely on AI Outputs as a substitute for professional medical evaluation or instruction</li>
        <li>Understand that improvement requires consistent practice, individual effort, and factors beyond any technology system</li>
      </ul>

      <hr />

      <h2>6. Data Used for AI Analysis</h2>
      <h3>6.1 What Data Is Processed</h3>
      <ul>
        <li>Video recordings you upload</li>
        <li>Body position and movement data derived from video analysis</li>
        <li>User-provided information (handicap, goals, practice history, equipment)</li>
        <li>Prior analysis results and progress data within your account</li>
      </ul>
      <h3>6.2 What Data Is Not Used</h3>
      <p>Your identifiable User Content is not used to train AI or machine learning models.</p>
      <h3>6.3 Third-Party AI Providers</h3>
      <p>
        The Service may use third-party AI model providers, subject to data processing agreements
        that restrict their use of your data.
      </p>

      <hr />

      <h2>7. Video Analysis Specific Disclosures</h2>
      <h3>7.1 Pose Detection</h3>
      <p>
        The Service uses pose detection technology to identify body landmarks and estimate joint
        positions from video. This is not equivalent to clinical biomechanical analysis performed
        with laboratory-grade motion capture equipment.
      </p>
      <h3>7.2 Biometric Data</h3>
      <p>
        Pose detection may generate data classified as "biometric data" under certain state laws.
        See the <Link to="/privacy#3-biometric-data">Biometric Data disclosures</Link> in our
        Privacy Policy.
      </p>

      <hr />

      <h2>8. Automated Decision-Making</h2>
      <p>
        The Service involves automated decision-making. AI systems analyze your video and data to
        generate assessments, recommendations, and practice plans without human intervention. These
        automated outputs may affect your practice experience, the drills you are shown, and the
        feedback you receive.
      </p>
      <p>
        You have the right to request human review of any AI Output that you believe significantly
        affects you. If the coach marketplace is available, you may seek human review through a
        marketplace Coach. You may also contact us with questions or concerns about any specific
        AI Output.
      </p>

      <hr />

      <h2>9. Model Information</h2>
      <h3>9.1 Model Version Transparency</h3>
      <p>
        You may request information about which AI model or model version produced a specific output
        by contacting us. We will provide reasonably available information about the model used,
        subject to the protection of proprietary systems and trade secrets.
      </p>
      <h3>9.2 Model Updates</h3>
      <p>
        AI models used by the Service are updated periodically. When a model update materially
        changes the type or nature of outputs (e.g., a new analysis methodology), we will disclose
        the change through the Service or in updates to this Addendum.
      </p>

      <hr />

      <h2>10. Changes to AI Features</h2>
      <p>
        AI features may change over time as models are updated. We may modify, suspend, or
        discontinue AI features at any time. Prior outputs are not guaranteed to be reproducible.
      </p>

      <hr />

      <h2>11. Contact</h2>
      <p>No Doubles Golf Co.</p>
      <p>Email: support@nodoublesgolf.com</p>
    </LegalPageLayout>
  );
}
